import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Languages, Mic, Camera, MessageSquareText } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api } from "../api";
import { toast } from "sonner";

/**
 * App Store Guidelines 5.1.1(i) / 5.1.2(i): before ANY content is sent to
 * OpenAI, the user must know exactly WHAT is shared, WITH WHOM and WHY —
 * and explicitly authorize it. Shown once at first sign-in; the choice can
 * be changed anytime in Settings.
 */
export const AIConsentDialog = () => {
  const { user, setUser, t } = useApp();
  const [busy, setBusy] = useState(false);

  const needsDecision = !!user && user.ai_consent !== true && user.ai_consent !== false;
  if (!needsDecision) return null;

  const decide = async (value) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { ai_consent: value });
      setUser({ ...user, ai_consent: value });
    } catch (e) {
      toast.error(t("ai_consent_error"));
    } finally {
      setBusy(false);
    }
  };

  const bullets = [
    { icon: Languages, text: t("ai_consent_b1") },
    { icon: Mic, text: t("ai_consent_b2") },
    { icon: MessageSquareText, text: t("ai_consent_b3") },
    { icon: Camera, text: t("ai_consent_b4") },
  ];

  return (
    <Dialog open>
      <DialogContent
        className="max-w-md rounded-2xl [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="ai-consent-dialog"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-base font-semibold leading-tight">{t("ai_consent_title")}</DialogTitle>
        </div>
        <DialogDescription asChild>
          <div className="space-y-3 text-left">
            <p className="text-sm text-foreground/90">{t("ai_consent_p1")}</p>
            <p className="text-xs text-muted-foreground">{t("ai_consent_p2")}</p>
            <ul className="space-y-1.5">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-foreground/85">
                  <b.icon className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                  <span>{b.text}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-300" data-testid="ai-consent-warning">
              {t("ai_consent_warning")}
            </div>
            <p className="text-[11px] text-muted-foreground">
              <Link to="/privacy" className="underline text-primary" data-testid="ai-consent-privacy-link">
                {t("ai_consent_privacy")}
              </Link>
            </p>
          </div>
        </DialogDescription>
        <div className="space-y-2 pt-1">
          <Button className="w-full h-11" disabled={busy} onClick={() => decide(true)} data-testid="ai-consent-accept-btn">
            {t("ai_consent_accept")}
          </Button>
          <Button variant="outline" className="w-full h-10" disabled={busy} onClick={() => decide(false)} data-testid="ai-consent-decline-btn">
            {t("ai_consent_decline")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
